/**
 * The JPEG header walk the API uses to refuse photos that still carry
 * metadata (rule 21), shared so the native app can run the same check on the
 * photo it just re-encoded before queueing it. One implementation: a client
 * that checked differently would queue photos the server then refuses.
 *
 * Only the header is read: segments up to Start Of Scan. A JPEG's metadata
 * sits there by construction; the scan data after it is pixels.
 */

export type JpegVerdict =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: 'not_jpeg' | 'exif' | 'truncated' };

const SOI = [0xff, 0xd8, 0xff];
const APP1 = 0xe1;
const SOS = 0xda;
const EOI = 0xd9;

/** Markers without a length field: TEM and RST0–RST7. */
function standalone(marker: number): boolean {
  return marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7);
}

/**
 * Walks the marker segments of `head` (the first bytes of the file) until
 * Start Of Scan. `not_jpeg` for a wrong signature or a broken segment
 * structure, `exif` for any APP1 segment, `truncated` when the header does
 * not end within `head` — the file is then not what the client produces, and
 * it is refused rather than read further.
 */
export function inspectJpeg(head: Uint8Array): JpegVerdict {
  if (head.length < 4 || SOI.some((b, i) => head[i] !== b))
    return { ok: false, reason: 'not_jpeg' };
  let i = 2;
  for (;;) {
    if (i >= head.length) return { ok: false, reason: 'truncated' };
    if (head[i] !== 0xff) return { ok: false, reason: 'not_jpeg' };
    // Fill bytes: any number of 0xFF before the marker.
    while (i < head.length && head[i] === 0xff) i += 1;
    if (i >= head.length) return { ok: false, reason: 'truncated' };
    const marker = head[i]!;
    i += 1;
    if (marker === APP1) return { ok: false, reason: 'exif' };
    if (marker === SOS || marker === EOI) return { ok: true };
    if (marker === 0x00) return { ok: false, reason: 'not_jpeg' };
    if (standalone(marker)) continue;
    if (i + 1 >= head.length) return { ok: false, reason: 'truncated' };
    const length = (head[i]! << 8) | head[i + 1]!;
    if (length < 2) return { ok: false, reason: 'not_jpeg' };
    i += length;
  }
}
